import { useEffect, useMemo, useState, type RefObject } from 'react'
import { useInView } from '../../../../../hooks/useInView'
import {
  EliteLandingPageState,
  type EliteConsentTextItem,
  type EliteReferralReward
} from '../../../../../types/misc.types'
import { ELITE_RIVE_URL, eliteLandingPageStateConfig } from '../../../../../constants'
import RiveComponent, { RiveFit } from '../../../../RiveComponent'
import { EliteButtonV3 } from '../../../EliteButtonV3'
import { useBottomSheet } from '../../../../../state-management/contexts/BottomSheetContext'
import { ESignLearnMoreContent } from '../../../../BottomSheetComponents/ESignLearnMoreContent'
import { convertEpochToDate } from '../../../../../utils/utils'
import { useSetAppBarTheme } from '../../../../../hooks/useSetAppBarTheme'
import { getTopPadding } from '../../../../../utils/sessionStorage'
import { EliteLandingJoinOfferStrip } from './EliteLandingJoinOfferStrip'

export const EliteLandingPageHeadV5 = ({
  handleEliteButtonVisibility,
  onBGRiveLoad,
  eliteLandingPageState,
  amount,
  cta,
  isForceUpdateRequired,
  version,
  isEliteButtonDisabled,
  activationExpiryDate,
  isEliteButtonVisible,
  consentText,
  consentTextList,
  familyMemberName,
  onConsentStateChange,
  referralReward,
  inlineEliteCtaRef
}: {
  handleEliteButtonVisibility: (x: boolean) => void
  onBGRiveLoad: () => void
  eliteLandingPageState: `${EliteLandingPageState}`
  amount: number
  cta: Record<any, any> | null
  isForceUpdateRequired: boolean
  version: 'V1' | 'V2' | 'V4'
  isEliteButtonDisabled?: boolean
  activationExpiryDate?: number
  isEliteButtonVisible: boolean
  consentText?: string
  consentTextList?: EliteConsentTextItem[]
  familyMemberName?: string
  onConsentStateChange?: (checked: boolean) => void
  referralReward?: EliteReferralReward | null
  /** Inline buy CTA — shared with page FAB bottom-band observer + sticky visibility. */
  inlineEliteCtaRef: RefObject<HTMLDivElement>
}) => {
  const isEliteButtonInView = useInView(inlineEliteCtaRef)
  const riveRatio = 1.74
  const [outsideRiveWidth, setOutsideRiveWidth] = useState(0)
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  const getAppBarTheme = () => {
    if (isEliteButtonInView === -1) {
      return null
    }
    return isEliteButtonInView ? 'light' : 'dark'
  }

  useSetAppBarTheme({ appBarTheme: getAppBarTheme() })

  useEffect(() => {
    if (isEliteButtonInView === -1) return
    handleEliteButtonVisibility(!isEliteButtonInView)
  }, [isEliteButtonInView])

  const bgRiveheight = useMemo(() => {
    return outsideRiveWidth * (riveRatio ?? 1) || 700
  }, [outsideRiveWidth])

  return (
    <div className='flex flex-col bg-[#010101] relative z-0 pb-10 -mt-16'>
      <div
        ref={(ref) => {
          if (ref && outsideRiveWidth === 0) {
            setOutsideRiveWidth(ref?.clientWidth ?? 300)
          }
        }}
        style={{ height: bgRiveheight }}
        className='absolute w-full'
      >
        <RiveComponent
          src={`${ELITE_RIVE_URL}/elite_bg.riv`}
          onLoad={onBGRiveLoad}
          fit={RiveFit.WIDTH}
          stateMachines={'animation_loop'}
        />
      </div>
      <div className='z-10 flex flex-col justify-center items-center w-full pt-[64px]'>
        <div
          style={{ paddingTop: `calc(${(outsideRiveWidth > 400 ? bgRiveheight * 0.1 : 56) + getTopPadding()}px)` }}
          className='h-auto w-full flex flex-col items-center justify-center'
        >
          <div className='h-20 w-20 ml-[6px]'>
            <img src={'/images/elite/elite_badge.png'} alt='' aria-hidden='true' className='h-full w-auto' />
          </div>
          <div className='w-[150px] min-h-[31px] h-max pt-3'>
            <img src={'/images/elite/elite_and_name.svg'} alt='Powerup Elite Logo' />
          </div>
        </div>
        <div className='text-[#FFF2DF] font-[500] text-[30px] text-center px-4 leading-[120%] pt-10'>
          Manage your portfolio <br /> like the top 1%
        </div>
        {/* <div className='pt-4 text-center text-[#FFF2DF] italic font-[500] opacity-50 px-4 text-[14px] leading-[140%] max-w-[330px]'>
          An exclusive membership for those who want to grow their wealth like the best.
        </div> */}
        <div className='pt-6 text-center text-[#FFF2DF] font-[400] opacity-80 px-4 text-[14px] leading-[160%] max-w-[330px]'>
          From selection, review to rebalancing - make mutual fund investing effortless with Elite
        </div>
        <EliteLandingJoinOfferStrip
          priceLabel={amount}
          cta={cta}
          isForceUpdateRequired={isForceUpdateRequired}
          version={version}
          isButtonDisabled={isEliteButtonDisabled}
        />
        <div ref={inlineEliteCtaRef} className='pt-8 pb-2 w-full px-5'>
          <EliteButtonV3
            amount={amount}
            eliteLandingPageState={eliteLandingPageState}
            cta={cta}
            isForceUpdateRequired={isForceUpdateRequired}
            isButtonDisabled={isEliteButtonDisabled}
            version={version}
            isEliteButtonVisible={isEliteButtonVisible}
            consentText={consentText}
            consentTextList={consentTextList}
            familyMemberName={familyMemberName}
            onConsentStateChange={onConsentStateChange}
            showConsentText={!isEliteButtonVisible}
            referralReward={referralReward}
          />
        </div>
        {eliteLandingPageStateConfig?.[eliteLandingPageState]?.supportText ? (
          <div className='flex flex-col items-center gap-2 px-5 w-11/12'>
            {eliteLandingPageState === 'PAYMENT_COMPLETED_SUBSCRIPTION_PENDING_WARNING' ? (
              <div className='text-[#F29A32] text-center text-[12px] font-[400] py-2'>
                {eliteLandingPageStateConfig?.[eliteLandingPageState]?.supportText?.replace(
                  '{activationExpiryDate}',
                  convertEpochToDate(activationExpiryDate ?? 0, '%d %b')
                )}
                <span
                  onClick={() =>
                    openBottomSheet(
                      <ESignLearnMoreContent onDismiss={closeBottomSheet} activationExpiryDate={activationExpiryDate} />
                    )
                  }
                  className='text-[#F29A32] text-[12px] font-[400] hover:opacity-80 transition-opacity underline cursor-pointer ml-1'
                >
                  Learn More
                </span>
              </div>
            ) : (
              <div className='text-[#F29A32] text-center text-[12px] font-[400] py-2'>
                {eliteLandingPageStateConfig?.[eliteLandingPageState]?.supportText}
              </div>
            )}
          </div>
        ) : (
          <div className='py-1'></div>
        )}
      </div>
    </div>
  )
}
